import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Receipt, Euro, AlertCircle, Plus, Trash2, CheckCircle2, Clock, ChevronDown } from 'lucide-react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { ListSkeleton, EmptyState } from '../components/ui';

const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

const formatEuro = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

const statoBadge = (stato) => {
  if (stato === 'pagato') return { background: 'var(--green-dim)', color: 'var(--green)' };
  if (stato === 'in_ritardo') return { background: 'var(--red-dim)', color: 'var(--red)' };
  return { background: 'var(--yellow-dim)', color: 'var(--yellow)' };
};

const statoLabel = (s) => s === 'pagato' ? 'Pagato' : s === 'in_ritardo' ? 'In ritardo' : 'Da pagare';

const currentYear = new Date().getFullYear();
const anni = Array.from({ length: 5 }, (_, i) => currentYear - 3 + i);

export default function Fatturazione() {
  const { user } = useAuth();
  const { success } = useToast();
  const [fatture, setFatture] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    cliente_id: '',
    mese: 1,
    anno: currentYear,
    importo: '',
    stato: 'da_pagare',
    note: ''
  });
  const [clientiList, setClientiList] = useState([]);
  const [filtroStato, setFiltroStato] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [trend, setTrend] = useState([]);
  const [generating, setGenerating] = useState(false);

  const loadData = () => {
    setLoading(true);
    Promise.all([
      api.get('/fatture'),
      api.get('/fatture/stats'),
      api.get('/clienti'),
      api.get('/fatture/mrr_trend').catch(() => ({ data: [] })),
    ]).then(([fRes, sRes, cRes, tRes]) => {
      setFatture(fRes.data || []);
      setStats(sRes.data || null);
      setClientiList((cRes.data || []).filter(c => c.stato === 'attivo'));
      setTrend(tRes.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  const generaRicorrenti = async () => {
    const now = new Date();
    if (!window.confirm(`Generare le fatture di ${now.toLocaleDateString('it-IT', { month:'long', year:'numeric' })} dai contratti attivi?`)) return;
    setGenerating(true);
    try {
      const r = await api.post(`/fatture/genera_ricorrenti?mese=${now.getMonth()+1}&anno=${now.getFullYear()}`);
      loadData();
      success(r.data.generate > 0 ? `${r.data.generate} fatture generate` : 'Fatture del mese già presenti');
    } catch {}
    setGenerating(false);
  };

  useEffect(() => { loadData(); }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setShowForm(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleFormChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/fatture', form);
      setShowForm(false);
      setForm({ cliente_id: '', mese: 1, anno: currentYear, importo: '', stato: 'da_pagare', note: '' });
      loadData();
      success('Fattura creata');
    } catch (_) {}
    setSubmitting(false);
  };

  const handleStatoChange = async (id, stato) => {
    try {
      await api.put(`/fatture/${id}`, { stato });
      setFatture(prev => prev.map(f => f.id === id ? { ...f, stato } : f));
      success(`Stato aggiornato: ${statoLabel(stato)}`);
    } catch (_) {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa fattura?')) return;
    try {
      await api.delete(`/fatture/${id}`);
      setFatture(prev => prev.filter(f => f.id !== id));
      success('Fattura eliminata');
    } catch (_) {}
  };

  const filtered = useMemo(() => {
    if (!filtroStato) return fatture;
    return fatture.filter(f => f.stato === filtroStato);
  }, [fatture, filtroStato]);

  const grouped = useMemo(() => {
    const byAnno = {};
    filtered.forEach(f => {
      const y = f.anno || currentYear;
      if (!byAnno[y]) byAnno[y] = {};
      const key = f.cliente_id || 'sconosciuto';
      if (!byAnno[y][key]) byAnno[y][key] = { nome: f.cliente_nome || f.azienda || '—', items: [] };
      byAnno[y][key].items.push(f);
    });
    return Object.keys(byAnno).sort((a, b) => b - a).map(anno => ({
      anno,
      clienti: Object.values(byAnno[anno])
    }));
  }, [filtered]);

  if (user?.ruolo !== 'admin') {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
        <AlertCircle size={40} style={{ marginBottom: 12, color: 'var(--red)' }} />
        <div>Accesso riservato agli amministratori.</div>
      </div>
    );
  }

  return (
    <div className="eden-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <Receipt size={28} color="var(--cyan)" />
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>Fatturazione</h1>
        <span style={{ background: 'var(--cyan-dim)', color: 'var(--cyan)', borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em' }}>ADMIN</span>
      </div>
      <div style={{ height: 16 }} />

      {/* Azioni + trend */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap', marginBottom:18 }}>
        <button onClick={generaRicorrenti} disabled={generating} className="eden-btn-primary" style={{ fontSize:12 }}>
          {generating ? 'Genero…' : 'Genera fatture del mese (da contratti attivi)'}
        </button>
        {trend.length > 0 && (() => {
          const max = Math.max(...trend.map(t => Number(t.totale) || 0), 1);
          return (
            <div style={{ display:'flex', alignItems:'flex-end', gap:5, height:46 }}>
              {trend.map((t, i) => {
                const h = Math.max(3, Math.round((Number(t.totale) || 0) / max * 42));
                const hi = Math.round((Number(t.incassato) || 0) / max * 42);
                return (
                  <div key={i} title={`${MESI[t.mese-1]} ${t.anno}: ${formatEuro(t.totale)} (incassato ${formatEuro(t.incassato)})`}
                    style={{ width:14, height:42, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
                    <div style={{ height:h, background:'var(--border-h)', borderRadius:'3px 3px 0 0', position:'relative' }}>
                      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:hi, background:'var(--cyan)', borderRadius:'3px 3px 0 0' }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
          <div className="eden-card" style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Euro size={16} color="var(--green)" />
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>MRR Totale</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--green)' }}>{formatEuro(stats.mrr_totale)}</div>
          </div>
          <div className="eden-card" style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Clock size={16} color="var(--yellow)" />
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Da pagare</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--yellow)' }}>{formatEuro(stats.da_pagare)}</div>
          </div>
          <div className="eden-card" style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <AlertCircle size={16} color="var(--red)" />
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>In ritardo</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--red)' }}>{formatEuro(stats.in_ritardo)}</div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ position: 'relative' }}>
          <select
            className="eden-select"
            value={filtroStato}
            onChange={e => setFiltroStato(e.target.value)}
            style={{ paddingRight: 32 }}
          >
            <option value="">Tutti</option>
            <option value="da_pagare">Da pagare</option>
            <option value="pagato">Pagato</option>
            <option value="in_ritardo">In ritardo</option>
          </select>
          <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
        </div>
        <div style={{ flex: 1 }} />
        <button className="eden-btn-primary" onClick={() => setShowForm(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={16} />
          Nuova fattura
        </button>
      </div>

      {showForm && (
        <div className="eden-card" style={{ padding: 24, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 20px', color: 'var(--text)', fontSize: 16, fontWeight: 700 }}>Nuova fattura</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Cliente</label>
                <div style={{ position: 'relative' }}>
                  <select
                    className="eden-select"
                    value={form.cliente_id}
                    onChange={e => handleFormChange('cliente_id', e.target.value)}
                    required
                    style={{ width: '100%', paddingRight: 32 }}
                  >
                    <option value="">Seleziona cliente...</option>
                    {clientiList.map(c => (
                      <option key={c.id} value={c.id}>{c.azienda || c.nome}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Importo (€)</label>
                <input
                  className="eden-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.importo}
                  onChange={e => handleFormChange('importo', e.target.value)}
                  required
                  style={{ width: '100%', boxSizing: 'border-box' }}
                  placeholder="0,00"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Mese</label>
                <div style={{ position: 'relative' }}>
                  <select
                    className="eden-select"
                    value={form.mese}
                    onChange={e => handleFormChange('mese', Number(e.target.value))}
                    style={{ width: '100%', paddingRight: 32 }}
                  >
                    {MESI.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                  <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Anno</label>
                <div style={{ position: 'relative' }}>
                  <select
                    className="eden-select"
                    value={form.anno}
                    onChange={e => handleFormChange('anno', Number(e.target.value))}
                    style={{ width: '100%', paddingRight: 32 }}
                  >
                    {anni.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Stato</label>
                <div style={{ position: 'relative' }}>
                  <select
                    className="eden-select"
                    value={form.stato}
                    onChange={e => handleFormChange('stato', e.target.value)}
                    style={{ width: '100%', paddingRight: 32 }}
                  >
                    <option value="da_pagare">Da pagare</option>
                    <option value="pagato">Pagato</option>
                    <option value="in_ritardo">In ritardo</option>
                  </select>
                  <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Note</label>
              <textarea
                className="eden-input"
                value={form.note}
                onChange={e => handleFormChange('note', e.target.value)}
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                placeholder="Note opzionali..."
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowForm(false)} style={{ background: 'var(--bg3)', color: 'var(--text-muted)', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 14 }}>
                Annulla
              </button>
              <button type="submit" className="eden-btn-primary" disabled={submitting}>
                {submitting ? 'Salvataggio...' : 'Salva fattura'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <ListSkeleton rows={6} />
      ) : grouped.length === 0 ? (
        <EmptyState icon={Receipt} title="Nessuna fattura trovata" hint="Le fatture registrate compaiono qui, raggruppate per anno e cliente." />
      ) : (
        grouped.map(({ anno, clienti }) => (
          <div key={anno} style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)' }}>{anno}</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
            {clienti.map((cliente, ci) => (
              <div key={ci} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, paddingLeft: 4 }}>
                  {cliente.nome}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {cliente.items.map(fattura => (
                    <div
                      key={fattura.id}
                      className="eden-card"
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px' }}
                    >
                      <div style={{ width: 100, color: 'var(--text-muted)', fontSize: 13 }}>
                        {MESI[(fattura.mese || 1) - 1]}
                      </div>
                      <div style={{ flex: 1, fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>
                        {formatEuro(fattura.importo)}
                      </div>
                      <span style={{
                        borderRadius: 6,
                        padding: '3px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        ...statoBadge(fattura.stato)
                      }}>
                        {statoLabel(fattura.stato)}
                      </span>
                      <div style={{ position: 'relative' }}>
                        <select
                          className="eden-select"
                          value={fattura.stato}
                          onChange={e => handleStatoChange(fattura.id, e.target.value)}
                          style={{ paddingRight: 28, fontSize: 12 }}
                        >
                          <option value="da_pagare">Da pagare</option>
                          <option value="pagato">Pagato</option>
                          <option value="in_ritardo">In ritardo</option>
                        </select>
                        <ChevronDown size={12} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
                      </div>
                      {fattura.note && (
                        <span style={{ color: 'var(--text-dim)', fontSize: 12, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {fattura.note}
                        </span>
                      )}
                      <button
                        onClick={() => handleDelete(fattura.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center' }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--red)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dim)'}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
